import type { Frequency, Layer, Unit } from "../types";

export type EcosCycle = "D" | "M" | "Q" | "A";

export interface EcosSelectorSpec {
  /** ECOS StatisticItemList group (Group1..Group4). Omit only when the selector is globally unique in the table. */
  groupCode?: string;
  itemCode?: string;
  expectedItemName?: string;
  resolveNames?: string[];
}

export interface EcosSeriesSpec {
  id: string;
  /** Canonical Shared Data Platform dataset. Keep UI ids independent from storage ids. */
  centralDatasetId: string;
  statCode: string;
  /** Legacy shorthand for a one-dimensional Group1 table. */
  itemCode?: string;
  expectedItemName?: string;
  resolveNames?: string[];
  /** Multi-dimensional selector. Every active metadata group must resolve, otherwise the series is rejected as N/A. */
  selectors?: EcosSelectorSpec[];
  cycle: EcosCycle;
  name: string;
  shortName: string;
  layer: Layer;
  unit: Unit;
  start: string;
  /** When set, query from the exact START_TIME resolved from current ECOS metadata instead of the conservative configured start. */
  historyStart?: "max";
  required: boolean;
  note?: string;
}

// Codes below are either directly verified from the user's current ECOS
// StatisticItemList output, or validated public ECOS examples where noted.
export const ECOS_SERIES = {
  bokBaseRate: {
    id: "bok_base_rate",
    centralDatasetId: "ecos.policy.base-rate.daily",
    statCode: "722Y001",
    itemCode: "0101000",
    expectedItemName: "한국은행 기준금리",
    cycle: "D",
    name: "한국은행 기준금리",
    shortName: "BOK",
    layer: "policy",
    unit: "%",
    start: "20100101",
    historyStart: "max",
    required: true,
    note: "Known ECOS base-rate code; runtime metadata validation is mandatory."
  },
  ktb1: {
    id: "ktb_1y",
    centralDatasetId: "ecos.rates.ktb-1y.daily",
    statCode: "817Y002",
    itemCode: "010190000",
    expectedItemName: "국고채(1년)",
    cycle: "D",
    name: "국고채 1년",
    shortName: "KTB 1Y",
    layer: "market",
    unit: "%",
    start: "20100101",
    required: true
  },
  ktb2: {
    id: "ktb_2y",
    centralDatasetId: "ecos.rates.ktb-2y.daily",
    statCode: "817Y002",
    itemCode: "010195000",
    expectedItemName: "국고채(2년)",
    cycle: "D",
    name: "국고채 2년",
    shortName: "KTB 2Y",
    layer: "market",
    unit: "%",
    start: "20210310",
    required: false
  },
  ktb3: {
    id: "ktb_3y",
    centralDatasetId: "ecos.rates.ktb-3y.daily",
    statCode: "817Y002",
    itemCode: "010200000",
    expectedItemName: "국고채(3년)",
    cycle: "D",
    name: "국고채 3년",
    shortName: "KTB 3Y",
    layer: "market",
    unit: "%",
    start: "20100101",
    required: true
  },
  ktb5: {
    id: "ktb_5y",
    centralDatasetId: "ecos.rates.ktb-5y.daily",
    statCode: "817Y002",
    itemCode: "010200001",
    expectedItemName: "국고채(5년)",
    cycle: "D",
    name: "국고채 5년",
    shortName: "KTB 5Y",
    layer: "market",
    unit: "%",
    start: "20100101",
    historyStart: "max",
    required: true
  },
  ktb10: {
    id: "ktb_10y",
    centralDatasetId: "ecos.rates.ktb-10y.daily",
    statCode: "817Y002",
    itemCode: "010210000",
    expectedItemName: "국고채(10년)",
    cycle: "D",
    name: "국고채 10년",
    shortName: "KTB 10Y",
    layer: "market",
    unit: "%",
    start: "20100101",
    required: true
  },
  corpAA3: {
    id: "corp_aa_3y",
    centralDatasetId: "ecos.credit.corp-aa-minus-3y.daily",
    statCode: "817Y002",
    itemCode: "010300000",
    expectedItemName: "회사채(3년, AA-)",
    cycle: "D",
    name: "회사채 3년 AA-",
    shortName: "Corp AA- 3Y",
    layer: "credit",
    unit: "%",
    start: "20100101",
    required: true
  },
  corpAA3Fair: {
    id: "corp_aa_3y_fair",
    centralDatasetId: "ecos.credit.corp-aa-minus-3y-fair.daily",
    statCode: "817Y002",
    itemCode: "010310000",
    expectedItemName: "회사채(3년, AA-, 민평)",
    cycle: "D",
    name: "회사채 3년 AA- 민평",
    shortName: "Corp AA- fair",
    layer: "credit",
    unit: "%",
    start: "20100101",
    required: false
  },
  corpBBB3: {
    id: "corp_bbb_3y",
    centralDatasetId: "ecos.credit.corp-bbb-minus-3y.daily",
    statCode: "817Y002",
    itemCode: "010320000",
    expectedItemName: "회사채(3년, BBB-)",
    cycle: "D",
    name: "회사채 3년 BBB-",
    shortName: "Corp BBB- 3Y",
    layer: "credit",
    unit: "%",
    start: "20100101",
    required: false
  },
  cd91: {
    id: "cd_91d",
    centralDatasetId: "ecos.rates.cd-91d.daily",
    statCode: "817Y002",
    itemCode: "010502000",
    expectedItemName: "CD(91일)",
    cycle: "D",
    name: "CD 91일",
    shortName: "CD 91D",
    layer: "market",
    unit: "%",
    start: "20100101",
    required: false
  },
  koribor12: {
    id: "koribor_12m",
    centralDatasetId: "ecos.rates.koribor-12m.daily",
    statCode: "817Y002",
    itemCode: "010152000",
    expectedItemName: "KORIBOR(12개월)",
    cycle: "D",
    name: "KORIBOR 12개월",
    shortName: "KORIBOR 12M",
    layer: "market",
    unit: "%",
    start: "20100101",
    required: false
  },
  usdKrw: {
    id: "usd_krw",
    centralDatasetId: "ecos.fx.usdkrw.daily",
    statCode: "731Y001",
    itemCode: "0000001",
    resolveNames: ["원/미국달러(매매기준율)", "원/달러(매매기준율)", "원/달러 환율"],
    cycle: "D",
    name: "원/달러 환율 매매기준율",
    shortName: "USD/KRW",
    layer: "market",
    unit: "krw_per_usd",
    start: "20100101",
    required: false,
    note: "ECOS 731Y001/0000001 매매기준율 계열. 고정 ITEM_CODE도 현재 StatisticItemList 명칭과 정확히 일치하는 후보일 때만 사용합니다."
  },
  kdb1: {
    id: "kdb_1y",
    centralDatasetId: "ecos.bank-funding.kdb-1y.daily",
    statCode: "817Y002",
    itemCode: "010260000",
    expectedItemName: "산금채(1년)",
    cycle: "D",
    name: "산금채 1년",
    shortName: "KDB 1Y",
    layer: "funding",
    unit: "%",
    start: "20100101",
    required: false,
    note: "보조 proxy. 일반 은행채 AAA와 동일시하지 않음."
  },
  fundingNew: {
    id: "bank_funding_new",
    centralDatasetId: "ecos.bank-funding.new.monthly",
    statCode: "121Y002",
    itemCode: "BEABAA224",
    expectedItemName: "금융채 3)",
    cycle: "M",
    name: "예금은행 금융채 신규취급금리",
    shortName: "Funding new",
    layer: "funding",
    unit: "%",
    start: "199601",
    required: true,
    note: "월중 신규 발행 금융채 조달금리. KOFIA 은행채 AAA 5Y와 다른 계열."
  },
  fundingOutstanding: {
    id: "bank_funding_outstanding",
    centralDatasetId: "ecos.bank-funding.outstanding.monthly",
    statCode: "121Y013",
    itemCode: "BEABAB2124",
    expectedItemName: "금융채 1)",
    cycle: "M",
    name: "예금은행 금융채 잔액금리",
    shortName: "Funding stock",
    layer: "funding",
    unit: "%",
    start: "200109",
    required: true,
    note: "기존 금융채 잔액에 깔린 평균 조달비용."
  },
  corpLoan: {
    id: "corp_loan_new",
    centralDatasetId: "ecos.loan.corporate.new-rate.monthly",
    statCode: "121Y006",
    resolveNames: ["기업대출"],
    cycle: "M",
    name: "예금은행 기업대출 신규취급금리",
    shortName: "Corp loan",
    layer: "credit",
    unit: "%",
    start: "200101",
    required: false
  },
  householdLoan: {
    id: "household_loan_new",
    centralDatasetId: "ecos.loan.household.new-rate.monthly",
    statCode: "121Y006",
    resolveNames: ["가계대출"],
    cycle: "M",
    name: "예금은행 가계대출 신규취급금리",
    shortName: "Household loan",
    layer: "credit",
    unit: "%",
    start: "200101",
    required: false
  },
  mortgageLoan: {
    id: "mortgage_loan_new",
    centralDatasetId: "ecos.mortgage.new-rate.monthly",
    statCode: "121Y006",
    resolveNames: ["주택담보대출"],
    cycle: "M",
    name: "예금은행 주택담보대출 신규취급금리",
    shortName: "Mortgage",
    layer: "credit",
    unit: "%",
    start: "200101",
    historyStart: "max",
    required: false
  },
  mortgageExistingCost: {
    id: "mortgage_existing_cost",
    centralDatasetId: "ecos.mortgage.outstanding-rate.monthly",
    statCode: "121Y015",
    itemCode: "BECBLB020202",
    expectedItemName: "주택담보대출",
    cycle: "M",
    name: "예금은행 주택담보대출 잔액금리",
    shortName: "Mortgage stock rate",
    layer: "real_estate",
    unit: "%",
    start: "200909",
    historyStart: "max",
    required: false,
    note: "2026-08-30 live ECOS StatisticItemList diagnostic에서 121Y015(예금은행 대출금리, 잔액 기준) / BECBLB020202(주택담보대출), 월별·연리%로 확인. 개별 차주의 약정금리가 아니라 예금은행 주담대 잔액의 가중평균 금리이므로 H1B에서는 existing-mortgage stock cost proxy로만 사용합니다."
  },
  mortgageStockVariableShare: {
    id: "mortgage_stock_repricing_exposure",
    centralDatasetId: "ecos.mortgage.variable-share.monthly",
    statCode: "121Y011",
    itemCode: "LR20000",
    expectedItemName: "주택담보대출-변동금리대출",
    cycle: "M",
    name: "예금은행 주택담보대출 변동금리대출 비중(잔액 기준)",
    shortName: "Mortgage stock variable",
    layer: "real_estate",
    unit: "%",
    start: "201301",
    required: false,
    note: "2026-08-30 live ECOS StatisticItemList diagnostic에서 121Y011(고정 및 변동금리대출 비중, 잔액 기준) / LR20000(주택담보대출-변동금리대출), 월별·%로 확인. 기존 mortgage stock의 변동금리 bucket 비중이며 정확한 개별 재가격일정 또는 월별 reset 비중으로 해석하지 않습니다."
  },
  housingPriceOutlookCsi: {
    id: "housing_price_outlook_csi",
    centralDatasetId: "ecos.sentiment.housing-price-outlook.monthly",
    statCode: "511Y002",
    selectors: [
      { groupCode: "Group1", itemCode: "FMFB", expectedItemName: "주택가격전망CSI" },
      { groupCode: "Group2", itemCode: "99988", expectedItemName: "전체" }
    ],
    cycle: "M",
    name: "주택가격전망 CSI",
    shortName: "Housing outlook CSI",
    layer: "real_estate",
    unit: "index",
    start: "201301",
    required: false,
    note: "2026-08-30T17:33Z 실배포 진단에서 511Y002는 Group1=FMFB(주택가격전망CSI)와 Group2=99988(전체)를 모두 요구하는 다차원 표로 확인되었습니다. 두 차원을 exact validation 후 사용하며 성별·연령·직업·소득별 하위집단을 전국 전체값으로 혼합하지 않습니다."
  },
  expectedInflation1Y: {
    id: "expected_inflation_households_1y",
    centralDatasetId: "ecos.expectations.inflation-1y.monthly",
    statCode: "511Y003",
    itemCode: "FMB",
    expectedItemName: "향후1년 기대인플레이션율",
    cycle: "M",
    name: "가계 기대인플레이션율(향후 1년)",
    shortName: "Expected inflation 1Y",
    layer: "real_economy",
    unit: "%",
    start: "200202",
    required: false,
    note: "2026-08-30 live ECOS StatisticItemList diagnostic에서 511Y003/FMB(향후1년 기대인플레이션율), 월별·%로 exact 확인. H1B는 1Y/3Y/5Y exact series를 모두 보존하고 runtime 선택기로 horizon을 선택합니다."
  },
  expectedInflation3Y: {
    id: "expected_inflation_households_3y",
    centralDatasetId: "ecos.expectations.inflation-3y.monthly",
    statCode: "511Y003",
    itemCode: "FMC",
    expectedItemName: "3년후 기대인플레이션율",
    cycle: "M",
    name: "가계 기대인플레이션율(3년 후)",
    shortName: "Expected inflation 3Y",
    layer: "real_economy",
    unit: "%",
    start: "201301",
    required: false,
    note: "2026-08-30 live ECOS StatisticItemList diagnostic에서 511Y003/FMC(3년후 기대인플레이션율), 월별·%로 exact 확인. H1B는 1Y/3Y/5Y exact series를 모두 보존하고 runtime 선택기로 horizon을 선택합니다."
  },
  expectedInflation5Y: {
    id: "expected_inflation_households_5y",
    centralDatasetId: "ecos.expectations.inflation-5y.monthly",
    statCode: "511Y003",
    itemCode: "FMD",
    expectedItemName: "5년후 기대인플레이션율",
    cycle: "M",
    name: "가계 기대인플레이션율(5년 후)",
    shortName: "Expected inflation 5Y",
    layer: "real_economy",
    unit: "%",
    start: "201301",
    required: false,
    note: "2026-08-30 live ECOS StatisticItemList diagnostic에서 511Y003/FMD(5년후 기대인플레이션율), 월별·%로 exact 확인. H1B는 1Y/3Y/5Y exact series를 모두 보존하고 runtime 선택기로 horizon을 선택합니다."
  },
  economicSentiment: {
    id: "economic_sentiment",
    centralDatasetId: "ecos.sentiment.economic.monthly",
    statCode: "513Y001",
    itemCode: "E1000",
    resolveNames: ["경제심리지수", "경제심리지수(원계열)"],
    cycle: "M",
    name: "경제심리지수",
    shortName: "ESI",
    layer: "real_economy",
    unit: "index",
    start: "200301",
    required: false,
    note: "E1000은 공개 ECOS 사용례에서 경제심리지수 원계열로 확인되며, 런타임 StatisticItemList 이름 검증을 통과할 때만 사용합니다."
  },
  industrialProduction: {
    id: "industrial_production",
    centralDatasetId: "ecos.production.all-industry-sa.monthly",
    statCode: "901Y033",
    selectors: [
      { groupCode: "Group1", itemCode: "A00", expectedItemName: "전산업생산지수(농림어업 제외)" },
      { groupCode: "Group2", itemCode: "2", expectedItemName: "계절조정" }
    ],
    cycle: "M",
    name: "전산업생산지수(농림어업제외, 계절조정)",
    shortName: "All-industry production SA",
    layer: "real_economy",
    unit: "index",
    start: "201001",
    required: false,
    note: "실배포 진단에서 901Y033 Group1=A00(전산업생산지수(농림어업 제외)), Group2=2(계절조정)가 현재 메타데이터로 확인되어 두 차원을 모두 exact validation 후 사용합니다."
  },
  unsoldTotal: {
    id: "unsold_total",
    centralDatasetId: "ecos.housing.unsold-total.monthly",
    statCode: "901Y074",
    selectors: [
      { resolveNames: ["전국"] }
    ],
    cycle: "M",
    name: "전국 미분양주택",
    shortName: "Unsold homes",
    layer: "real_estate",
    unit: "housing_units",
    start: "201001",
    required: false,
    note: "901Y074는 미분양주택현황 표입니다. v0.5.1 실제 진단 ZIP에서 '준공후 미분양'이라는 별도 ECOS dimension 이름이 확인되지 않아 총 미분양으로 정정했습니다. 추가 활성 Group이 있으면 임의 선택하지 않고 N/A와 후보를 진단 이슈에 표시합니다. 준공후 미분양은 별도 국토교통부 원자료 연결 대상으로 남깁니다."
  }
} satisfies Record<string, EcosSeriesSpec>;

export const CORE_ECOS_SPECS = Object.values(ECOS_SERIES);
